import React, {
  useEffect,
} from 'react';
import {
  Row,
  Table,
} from '@tanstack/react-table';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import TotalCell from 'pages/Dashboard/pages/Billing/Charges/columns/TotalCell';
import Details from 'pages/Dashboard/pages/Billing/Charges/columns/Details';
import ClaimRow from 'pages/Dashboard/pages/Billing/Charges/columns/ClaimRow';
import ServiceCell from 'pages/Dashboard/pages/Billing/Charges/columns/ServiceCell';
import {
  ClaimRowData,
} from 'pages/Dashboard/pages/Billing/types';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import CopyWrapper from 'core/components/CopyWrapper';
import {
  patientColumn,
} from 'pages/Dashboard/pages/Billing/Charges/columns';
import {
  useClaimDetails,
} from 'pages/Dashboard/pages/Billing/Charges/RowDetailsProvider';
import isNil from 'lodash/isNil';
import {
  ExpandAllButton,
  ExpandRowButton,
  useExpandAllDetails,
} from 'pages/Dashboard/pages/Billing/Charges/columns/ExpandToggle';

export function getSelectAllColumnTitle<T>(table: Table<T>) {
  const topLevelRows = table.getCoreRowModel().rows;
  const selectedCount = topLevelRows.filter((row) => row.getIsSelected()).length;
  const isNoRowsSelected = selectedCount === 0;
  const isAllRowsSelected = topLevelRows.length > 0 && selectedCount === topLevelRows.length;
  const isSomeRowsSelected = selectedCount > 0 && !isAllRowsSelected;

  return (
    <Checkbox
      id="select-all"
      checked={isAllRowsSelected}
      indeterminate={isSomeRowsSelected}
      onChange={(checked, event) => {
        event?.stopPropagation();
        topLevelRows.forEach((row) => row.toggleSelected(isNoRowsSelected));
        billingEventChannel.emit('onClaimsRowsSelectionChange');
      }}
    />
  );
}

const selectRowColumn: ColumnDef<ClaimRowData> = {
  id: 'selectRow',
  size: 32,
  columnTitle: getSelectAllColumnTitle,
  cell: ({ row }) => {
    const { isChild } = row.original ?? {};

    return !isChild && (
      <Checkbox
        id="select-row"
        classes={{ root: 'mt-1.5' }}
        checked={Boolean(row.getIsSelected())}
        onClick={(event) => {
          event?.stopPropagation();
        }}
        onChange={(checked, event) => {
          event?.stopPropagation();
          event?.preventDefault();
          row.toggleSelected();
          billingEventChannel.emit('onClaimsRowsSelectionChange');
        }}
      />
    );
  },
};

type RowWithClaimId = {
  claimId?: number;
};

type ToggleAllColumnTitleProps<T> = {
  table: Table<T>;
};

function ToggleAllColumnTitle<T>({ table }: ToggleAllColumnTitleProps<T>) {
  const { requestDetails } = useClaimDetails();

  useExpandAllDetails(
    table,
    requestDetails,
    (row) => (row as RowWithClaimId).claimId ?? 0,
  );

  return <ExpandAllButton table={table} />;
}

export function getToggleColumnTitle<T>(table: Table<T>) {
  return <ToggleAllColumnTitle table={table} />;
}

type ToggleRowCellProps = {
  row: Row<ClaimRowData>;
  table: Table<ClaimRowData>;
};

function ToggleRowCell({ row, table }: ToggleRowCellProps) {
  const { requestDetails, detailsById } = useClaimDetails();
  const claimId = row.original.claimId ?? 0;
  const expandable = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const isAllRowsExpanded = table.getIsAllRowsExpanded();
  const { isChild } = row.original ?? {};

  useEffect(() => {
    if (
      !isAllRowsExpanded
      && isExpanded
      && expandable
      && claimId > 0
      && isNil(detailsById[claimId])
    ) {
      requestDetails([claimId]);
    }
  }, [
    isAllRowsExpanded,
    isExpanded,
    expandable,
    claimId,
    detailsById,
    requestDetails,
  ]);

  return !isChild && (
    <ExpandRowButton
      expandable={expandable}
      isExpanded={isExpanded}
      onToggle={(event) => {
        event.stopPropagation();
        row.toggleExpanded();
      }}
    />
  );
}

const toggleRowColumn: ColumnDef<ClaimRowData> = {
  id: 'toggleRow',
  size: 40,
  columnTitle: getToggleColumnTitle,
  cell: ({ row, table }) => <ToggleRowCell row={row} table={table} />,
};

type ClaimDetailsCellProps = {
  row: Row<ClaimRowData>;
};

function ClaimDetailsCell({ row }: ClaimDetailsCellProps) {
  return row.original.isChild ? (
    <ServiceCell {...row.original} />
  ) : (
    <div className="flex flex-col gap-2 !truncate max-w-full">
      <Details {...row.original} />
      <ClaimRow
        claim={row.original}
        patientId={row.original.patientId}
        hideId
      />
    </div>
  );
}

const detailsColumn: ColumnDef<ClaimRowData> = {
  id: 'details',
  accessorKey: 'encounterId',
  className: 'mt-2',
  meta: { autoSized: true },
  columnTitle: 'Details',
  cell: ({ row }) => <ClaimDetailsCell row={row} />,
};

const claimIdColumn: ColumnDef<ClaimRowData> = {
  id: 'externalId',
  accessorKey: 'externalId',
  minSize: 180,
  className: 'mt-2',
  columnTitle: 'Claim',
  size: 220,
  cell: ({ row: { original } }) => {
    const { isChild } = original;

    return !isChild && (
      <CopyWrapper
        text={original?.externalId ?? ''}
        tooltip="Copy claim ID"
        className="copy-wrapper"
      >
        <div className="w-fit !truncate text-gray-700 font-semibold py-1 px-3 bg-gray-100 rounded-lg">{original?.externalId}</div>
      </CopyWrapper>
    );
  },
};

const billedAmountColumn: ColumnDef<ClaimRowData> = {
  id: 'billedAmount',
  accessorKey: 'billedAmount',
  className: 'mt-2',
  size: 120,
  columnTitle: (
    <>
      Billed
      <br />
      Amount
    </>
  ),
  multiLineHeader: true,
  cell: ({ row }) => {
    const {
      billedAmount = 0,
      isDetailsLoading,
    } = row.original ?? {};

    return isDetailsLoading ? null : (
      <TotalCell
        {...row.original}
        total={billedAmount}
        isTag
        useColors={false}
      />
    );
  },
};

const allowedAmountColumn: ColumnDef<ClaimRowData> = {
  id: 'allowedAmount',
  size: 120,
  className: 'mt-2',
  accessorKey: 'allowedAmount',
  columnTitle: (
    <>
      Allowed
      <br />
      Amount
    </>
  ),
  multiLineHeader: true,
  cell: ({ row: { original } }) => {
    const {
      allowedAmount = 0,
      isDetailsLoading,
    } = original;

    return isDetailsLoading ? null : (
      <TotalCell
        {...original}
        total={allowedAmount}
        isTag
      />
    );
  },
};
const insurancePaymentColumn: ColumnDef<ClaimRowData> = {
  id: 'insurancePayment',
  accessorKey: 'insurancePaidAmount',
  className: 'mt-2',
  columnTitle: (
    <>
      Insurance
      <br />
      Amount
    </>
  ),
  multiLineHeader: true,
  size: 120,
  cell: ({ row: { original } }) => {
    const {
      insurancePaidAmount = 0,
      isDetailsLoading,
    } = original;

    return isDetailsLoading ? null : (
      <TotalCell
        {...original}
        total={insurancePaidAmount}
        isTag
      />
    );
  },
};

export default [
  selectRowColumn,
  toggleRowColumn,
  patientColumn,
  claimIdColumn,
  detailsColumn,
  billedAmountColumn,
  allowedAmountColumn,
  insurancePaymentColumn,
] as ColumnDef<ClaimRowData>[];
